/**
 * Smooth momentum scroll for mouse wheels (merchant request: a soft "after-scroll" glide).
 *
 * - Desktop pointers only; touch devices and trackpads keep their native momentum.
 * - Off when the visitor prefers reduced motion, while a modal dialog is open,
 *   with Ctrl/⌘ (zoom), and for horizontal wheel movement.
 * - Hands the wheel back to any inner element that can still scroll in that direction
 *   (sidebar menu, search results, sliders, filter drawer).
 * - Native scrolling, keyboard, scrollbar dragging and anchor links keep working;
 *   the glide simply re-syncs to wherever the page actually is.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

const EASE = 0.06; // share of the remaining distance covered per frame (lower = longer, more dramatic glide)
const LINE_HEIGHT = 16;

let target = window.scrollY;
let current = window.scrollY;
let rafId = 0;

function maxScroll() {
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

function canScrollInside(node, deltaY) {
  while (node && node !== document.body && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      const style = getComputedStyle(node);
      const scrollable = /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
      if (scrollable) {
        if (deltaY < 0 && node.scrollTop > 0) return true;
        if (deltaY > 0 && node.scrollTop + node.clientHeight < node.scrollHeight - 1) return true;
      }
    }
    node = node.parentNode instanceof ShadowRoot ? node.parentNode.host : node.parentNode;
  }
  return false;
}

function looksLikeTrackpad(event) {
  // Trackpads send small, pixel-precise deltas and already have native momentum.
  return event.deltaMode === 0 && Math.abs(event.deltaY) < 50;
}

function step() {
  current += (target - current) * EASE;
  if (Math.abs(target - current) < 0.5) {
    current = target;
    window.scrollTo({ top: current, behavior: "instant" });
    rafId = 0;
    return;
  }
  window.scrollTo({ top: current, behavior: "instant" });
  rafId = requestAnimationFrame(step);
}

function onWheel(event) {
  if (reducedMotion.matches || !finePointer.matches) return;
  if (event.defaultPrevented || event.ctrlKey || event.metaKey) return;
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
  if (document.querySelector('dialog[open]')) return;
  if (looksLikeTrackpad(event)) return;
  if (canScrollInside(event.target, event.deltaY)) return;

  event.preventDefault();

  if (!rafId) {
    // Re-sync with wherever native scrolling, keys or anchors left the page.
    current = window.scrollY;
    target = window.scrollY;
  }

  const unit = event.deltaMode === 1 ? LINE_HEIGHT : event.deltaMode === 2 ? window.innerHeight : 1;
  target = Math.min(maxScroll(), Math.max(0, target + event.deltaY * unit));

  if (!rafId) rafId = requestAnimationFrame(step);
}

function stop() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
  current = window.scrollY;
  target = window.scrollY;
}

window.addEventListener('wheel', onWheel, { passive: false });
// Any non-wheel interaction takes over immediately so the glide never fights the visitor.
['keydown', 'mousedown', 'touchstart'].forEach((type) => window.addEventListener(type, stop, { passive: true }));
window.addEventListener('resize', stop, { passive: true });
